import { useRef, useState } from 'react';

import { Box, Collapse, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { radius } from '@karehero/llama/theme/radius';

import ChatOption from '@/karehero-app/tell-us-once/intake/ChatOption';
import { type ChatQuestion } from '@/karehero-app/tell-us-once/intake/content';

type Option = NonNullable<ChatQuestion['asides']>[number];

/**
 * Past this many, the answers start behind one See options button and open in
 * the chat when it is tapped. Five attached under the bubble as it arrives push
 * the question off the top before it has been read; by the time someone taps
 * See options, they have read it.
 */
export const MENU_ATTACH_MAX = 4;

/** How long the rows they did not pick take to fold away. */
export const MENU_FOLD_MS = 240;

const SEE_OPTIONS = 'See options';
const DONE = 'Done';

/** "a, b and c", the way it's said, and back again. */
const joined = (items: string[]) =>
  items.length < 2
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
const parts = (text: string) => text.split(/, | and /);

// Scrolls whichever box the chat scrolls in just far enough to show `el`.
// Not scrollIntoView: that scrolls every ancestor, which on a flows page drags
// the whole page to the mini. Measured on screen and divided back down, since
// a mini is drawn scaled.
const showWithin = (el: HTMLElement) => {
  let box = el.parentElement;
  while (box && !/(auto|scroll)/.test(getComputedStyle(box).overflowY)) {
    box = box.parentElement;
  }
  if (!box) return;
  const scale = box.getBoundingClientRect().height / box.clientHeight || 1;
  const below =
    (el.getBoundingClientRect().bottom - box.getBoundingClientRect().bottom) /
    scale;
  if (below > 0) box.scrollBy({ top: below + 12, behavior: 'smooth' });
};

/**
 * A message from Keni with its answers attached underneath, the way WhatsApp
 * draws a message with buttons: the bubble, then one button per answer joined
 * to it, label centred, plus anything else to do (`more`) in quieter rows.
 *
 * For choosing an answer from a set. A reply that moves the conversation on
 * (Yes, Not right now) is a chip, not one of these.
 *
 * More answers than `MENU_ATTACH_MAX` start behind a See options button, which
 * opens them in the chat, under the message, and scrolls them into view.
 * Once answered, the rest fold away and the one they picked
 * stays, marked, so the history reads as decided rather than still open.
 */
const ChatMenuMessage = ({
  title,
  text,
  options,
  more = [],
  keep = [],
  onPick,
  onMore,
  picked,
  defaultOpen = false,
  reopened = false,
  onReopen,
  onChange,
  many = false,
  ticked: tickedAtFirst = [],
}: {
  title?: string;
  text: string;
  options: Option[];
  /** Buttons after the options that are not options, like Learn more. */
  more?: string[];
  /**
   * Rows from `more` that stay once it is answered, and still work: something
   * to come back to, like How we use AI, rather than part of the choice.
   */
  keep?: string[];
  /** Absent once answered: the buttons stay, but do nothing. */
  onPick?: (label: string) => void;
  /**
   * A `more` row that opens something rather than answering, like How we use
   * AI. Absent, a more row answers like any other.
   */
  onMore?: (label: string) => boolean;
  /** What they picked, once they have. */
  picked?: string;
  /** Harness-only: open with See options already tapped. */
  defaultOpen?: boolean;
  /**
   * Answered, and open again to change it: every row back, the one they
   * picked still marked. Held by the chat, so tapping their reply can open it.
   */
  reopened?: boolean;
  /** Answered, tapping the row they picked opens the rest again. */
  onReopen?: () => void;
  /** Open again, another row picked: the answer changes to it. */
  onChange?: (label: string) => void;
  /**
   * More than one can be picked. A tap ticks a row, and Done sends them
   * together, as one reply. A quiet row stands alone and answers straight
   * away.
   */
  many?: boolean;
  /** Harness-only: with many, the rows already ticked. */
  ticked?: string[];
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const behind = options.length > MENU_ATTACH_MAX;
  const [opened, setOpened] = useState(defaultOpen);
  // Answered, the answers are out whether or not this one saw them open: an
  // answer in the history shows as the row they picked.
  const out = !behind || opened || Boolean(picked);
  const live = Boolean(onPick) && !picked;
  const [ticked, setTicked] = useState(tickedAtFirst);
  // Answered with several, each one they picked stays, ticked.
  const pickedAll = picked ? (many ? parts(picked) : [picked]) : [];

  const row = ({
    label,
    quiet = false,
    icon,
    shown = true,
    onClick,
    onShown,
  }: {
    label: string;
    quiet?: boolean;
    icon?: string;
    shown?: boolean;
    onClick: () => void;
    onShown?: () => void;
  }) => {
    const chosen =
      pickedAll.includes(label) || (live && ticked.includes(label));
    // Answered, everything but the one they picked folds away, so the menu
    // goes down to their answer.
    const kept = keep.includes(label);
    const stays = shown && (!picked || reopened || chosen || kept);
    // Answered rows can be tapped again where the answer can be changed.
    const changeable = Boolean(picked) && Boolean(onChange);
    return (
      <Collapse
        key={label}
        in={stays}
        timeout={MENU_FOLD_MS}
        onEntered={onShown}
      >
        {/* The gap lives inside the fold, so a folded row takes its gap with
            it rather than leaving it behind. */}
        <Box sx={{ pt: 0.5 }}>
          <ChatOption
            label={label}
            quiet={quiet}
            picked={chosen}
            icon={icon}
            disabled={!live && !kept && !changeable}
            onClick={onClick}
          />
        </Box>
      </Collapse>
    );
  };

  return (
    <Stack
      ref={ref}
      sx={{ alignSelf: 'flex-start', width: '86%' }}
      role='group'
      aria-label={text}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'custom.neutral.200',
          borderRadius: `${radius.lg}px`,
          borderBottomLeftRadius: '4px',
        }}
      >
        <Stack spacing={0.5}>
          {title && (
            <Typography sx={{ color: 'text.primary' }} variant='subtitle1'>
              {title}
            </Typography>
          )}
          <Typography
            sx={{ color: 'text.primary', whiteSpace: 'pre-line' }}
            variant='body1'
          >
            {text}
          </Typography>
        </Stack>
      </Box>

      {/* One button for the lot, until it is tapped. It folds away as the
          answers open in its place. */}
      {behind &&
        row({
          label: SEE_OPTIONS,
          icon: 'list',
          shown: !out,
          onClick: () => setOpened(true),
        })}

      {options.map((o, i) =>
        row({
          label: o.label,
          quiet: o.quiet,
          shown: out,
          onClick: () => {
            if (picked && onChange) {
              if (!reopened) onReopen?.();
              else onChange(o.label);
              return;
            }
            if (many && !o.quiet) {
              setTicked((t) =>
                t.includes(o.label)
                  ? t.filter((x) => x !== o.label)
                  : [...t, o.label],
              );
              return;
            }
            onPick?.(o.label);
          },
          // Once the last one is open, the chat moves to show them all.
          onShown:
            behind && i === options.length - 1
              ? () => ref.current && showWithin(ref.current)
              : undefined,
        }),
      )}

      {/* Sends what is ticked, once something is. A button rather than
          another row, so it reads as the way on, not one more answer. */}
      {many && (
        <Collapse in={live && ticked.length > 0} timeout={MENU_FOLD_MS}>
          <Box sx={{ pt: 1 }}>
            <Button
              variant='primary'
              isFullWidth
              ariaLabel={DONE}
              onPress={() =>
                onPick?.(
                  joined(
                    options
                      .map((o) => o.label)
                      .filter((l) => ticked.includes(l)),
                  ),
                )
              }
            >
              {DONE}
            </Button>
          </Box>
        </Collapse>
      )}

      {more.map((label) =>
        row({
          label,
          quiet: true,
          onClick: () => {
            if (!onMore?.(label)) onPick?.(label);
          },
        }),
      )}
    </Stack>
  );
};

ChatMenuMessage.displayName = 'ChatMenuMessage';
export default ChatMenuMessage;
